'use client';

import { c as _c } from "react/compiler-runtime";
import CommentBlockTextarea from '@/app/[locale]/(worlds)/[roomId]/components/CommentBlockTextarea';
import CommentLikeButton from '@/app/[locale]/(worlds)/[roomId]/components/CommentLikeButton';
import CommentMoreActionsButton from '@/app/[locale]/(worlds)/[roomId]/components/CommentMoreActionsButton';
import CommentTitle from '@/app/[locale]/(worlds)/[roomId]/components/CommentTitle';
import DeleteCommentConfirmModal from '@/app/[locale]/(worlds)/[roomId]/components/DeleteCommentConfirmModal';
import DeleteThreadConfirmModal from '@/app/[locale]/(worlds)/[roomId]/components/DeleteThreadConfirmModal';
import IconBack from '@/components/assets/icon_back.svg';
import { LoadingDots } from '@/components/LoadingDots';
import ReadMoreText from '@/components/ReadMoreText';
import ReportConfirmationModal from '@/components/ReportConfirmationModal';
import ReportModal from '@/components/ReportModal';
import SignInConfirmModal from '@/components/SignInConfirmModal';
import ViverseIconButton from '@/components/ViverseIconButton';
import { ReportReason } from '@/constants/comment';
import { useAuthContext } from '@/context/AuthContext';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useVideoCommentsPanelContext } from '@/context/VideoCommentsPanelContext';
import { cn } from '@/lib/utils';
import { Comment } from '@/types/comments';
import { useTranslations } from 'next-intl';
import { ReactNode, useEffect, useState } from 'react';
import { useIsClient, useMediaQuery } from 'usehooks-ts';
type CommentBlockProps = {
  comment: Comment;
  isReply: boolean;
  addComment: (value: string) => Promise<void>;
  removeComment: (commentId: string) => void;
  updateCommentLikeCount: (commentId: string, isLiked: boolean) => void;
  reportComment: (commentId: string, reason: ReportReason, detail: string) => void;
  children?: ReactNode;
};
function CommentBlock(t0) {
  const $ = _c(89);
  const {
    comment,
    isReply,
    addComment,
    removeComment,
    updateCommentLikeCount,
    reportComment,
    children
  } = t0;
  const t = useTranslations();
  const isTablet = useMediaQuery("(min-width: 768px)");
  const {
    store
  } = useConfigsContext();
  const {
    roomData
  } = useCheckEnterRoomContext();
  const {
    currentUserId,
    isSignedIn
  } = useAuthContext();
  const {
    panelHostEl
  } = useVideoCommentsPanelContext();
  const isClient = useIsClient();
  const [isLiked, setIsLiked] = useState(comment.is_liked || false);
  const [likeCount, setLikeCount] = useState(comment.like_count || 0);
  const [isReplying, setIsReplying] = useState(false);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showRemoveCommentModal, setShowRemoveCommentModal] = useState(false);
  const [showReportConfirmationModal, setShowReportConfirmationModal] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const isRemovedByUser = comment.is_reported;
  const isRemovedByMod = comment.delete_by_mod;
  const isRemoved = isRemovedByUser || isRemovedByMod;
  let t1;
  if ($[0] !== comment.content || $[1] !== isRemovedByMod || $[2] !== isRemovedByUser || $[3] !== t) {
    t1 = isRemovedByMod ? t("comment-block.removed-by-mod") : isRemovedByUser ? t("comment-block.removed-by-user") : comment.content;
    $[0] = comment.content;
    $[1] = isRemovedByMod;
    $[2] = isRemovedByUser;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const commentContent = t1;
  let t2;
  if ($[5] !== addComment || $[6] !== isSignedIn) {
    t2 = async value => {
      if (isSignedIn) {
        setIsAddingComment(true);
        await addComment(value);
        setIsReplying(false);
        setIsAddingComment(false);
      } else {
        setShowSignInModal(true);
      }
    };
    $[5] = addComment;
    $[6] = isSignedIn;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleAddComment = t2;
  let t3;
  if ($[8] !== isSignedIn) {
    t3 = () => {
      if (isSignedIn) {
        setIsReplying(_temp);
      } else {
        setShowSignInModal(true);
      }
    };
    $[8] = isSignedIn;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleClickReply = t3;
  let t4;
  if ($[10] !== comment.id || $[11] !== isLiked || $[12] !== isSignedIn || $[13] !== store?.credentials?.htcToken || $[14] !== updateCommentLikeCount) {
    t4 = async () => {
      const htcToken = store?.credentials?.htcToken;
      if (!(isSignedIn && htcToken)) {
        setShowSignInModal(true);
        return;
      }
      ;
      try {
        setIsLiked(_temp2);
        setLikeCount(prev_3 => prev_3 + (isLiked ? -1 : 1));
        await updateCommentLikeCount(comment.id, !isLiked);
      } catch (t5) {
        const err = t5;
        setIsLiked(_temp3);
        setLikeCount(prev_1 => prev_1 - (isLiked ? -1 : 1));
        console.error("Failed to update like count:", err);
      }
    };
    $[10] = comment.id;
    $[11] = isLiked;
    $[12] = isSignedIn;
    $[13] = store?.credentials?.htcToken;
    $[14] = updateCommentLikeCount;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleClickLike = t4;
  let t5;
  if ($[16] !== reportComment) {
    t5 = (commentId, reason, detail) => {
      reportComment(commentId, reason, detail);
      setShowReportConfirmationModal(true);
    };
    $[16] = reportComment;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleReportSubmit = t5;
  let t6;
  let t7;
  if ($[18] !== comment.is_liked || $[19] !== comment.like_count) {
    t6 = () => {
      setIsLiked(comment.is_liked || false);
      setLikeCount(comment.like_count);
    };
    t7 = [comment.is_liked, comment.like_count];
    $[18] = comment.is_liked;
    $[19] = comment.like_count;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  useEffect(t6, t7);
  const t8 = isClient && isTablet;
  const t9 = !!currentUserId && currentUserId === comment.owner.community_user_id;
  const t10 = comment.owner.community_user_id === roomData?.owner?.community_user_id;
  let t11;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setShowRemoveCommentModal(true);
    t12 = () => setIsReportModalOpen(true);
    $[22] = t11;
    $[23] = t12;
  } else {
    t11 = $[22];
    t12 = $[23];
  }
  let t13;
  if ($[24] !== comment) {
    t13 = <CommentMoreActionsButton comment={comment} handleDelete={t11} handleReport={t12} />;
    $[24] = comment;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== comment || $[27] !== isRemoved || $[28] !== t10 || $[29] !== t13 || $[30] !== t8 || $[31] !== t9) {
    t14 = <CommentTitle isTablet={t8} isRemoved={isRemoved} isYou={t9} isCreator={t10} comment={comment}>{t13}</CommentTitle>;
    $[26] = comment;
    $[27] = isRemoved;
    $[28] = t10;
    $[29] = t13;
    $[30] = t8;
    $[31] = t9;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const t15 = isRemoved && "text-vive-primary-60";
  let t16;
  if ($[33] !== t15) {
    t16 = cn("body1-omni-styles text-vive-white mr-12", t15);
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== commentContent || $[36] !== t16) {
    t17 = <ReadMoreText content={commentContent} className={t16} />;
    $[35] = commentContent;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== handleClickLike || $[39] !== handleClickReply || $[40] !== isLiked || $[41] !== isRemoved || $[42] !== isReplying || $[43] !== likeCount || $[44] !== t) {
    t18 = !isRemoved && <div className="flex items-center gap-1"><CommentLikeButton isLiked={isLiked} handleClick={handleClickLike} likeCount={likeCount} /><ViverseIconButton size="custom" variant="empty" className={cn("buttonS-omni-styles h-8.5 w-fit gap-1 px-2 py-1", isReplying && "bg-rgba(110,124,142,0.15) text-vive-primary-40")} labelClassName={cn("group-hover:text-vive-white group-active:text-vive-primary-40", isReplying && "text-vive-primary-40")} label={t("comment-block.reply")} onClick={handleClickReply}><IconBack className={cn("[&>path]:fill-vive-primary-20 [&>path]:group-hover:fill-vive-white [&>path]:group-active:fill-vive-primary-40 size-5", isReplying && "[&>path]:fill-vive-primary-40")} /></ViverseIconButton></div>;
    $[38] = handleClickLike;
    $[39] = handleClickReply;
    $[40] = isLiked;
    $[41] = isRemoved;
    $[42] = isReplying;
    $[43] = likeCount;
    $[44] = t;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== comment.id || $[47] !== handleAddComment || $[48] !== isRemoved || $[49] !== isReplying) {
    t19 = isReplying && !isRemoved && <CommentBlockTextarea textAreaElementId={comment.id} addComment={handleAddComment} />;
    $[46] = comment.id;
    $[47] = handleAddComment;
    $[48] = isRemoved;
    $[49] = isReplying;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== comment.id || $[52] !== isReply || $[53] !== panelHostEl || $[54] !== removeComment || $[55] !== showRemoveCommentModal) {
    t20 = isReply ? <DeleteCommentConfirmModal open={showRemoveCommentModal} onAction={() => {
      removeComment(comment.id);
      setShowRemoveCommentModal(false);
    }} onCancel={() => {
      setShowRemoveCommentModal(false);
    }} container={panelHostEl} /> : <DeleteThreadConfirmModal open={showRemoveCommentModal} onAction={() => {
      removeComment(comment.id);
      setShowRemoveCommentModal(false);
    }} onCancel={() => {
      setShowRemoveCommentModal(false);
    }} container={panelHostEl} />;
    $[51] = comment.id;
    $[52] = isReply;
    $[53] = panelHostEl;
    $[54] = removeComment;
    $[55] = showRemoveCommentModal;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setShowSignInModal(false);
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== t) {
    t22 = t("sign-in-confirm-modal.description");
    $[58] = t;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== panelHostEl || $[61] !== showSignInModal || $[62] !== t22) {
    t23 = <SignInConfirmModal open={showSignInModal} onClose={t21} content={t22} container={panelHostEl} />;
    $[60] = panelHostEl;
    $[61] = showSignInModal;
    $[62] = t22;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setIsReportModalOpen(false);
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== comment.id || $[66] !== handleReportSubmit || $[67] !== isReportModalOpen || $[68] !== panelHostEl) {
    t25 = <ReportModal commentId={comment.id} isDialogOpen={isReportModalOpen} onSubmit={handleReportSubmit} onClose={t24} container={panelHostEl} />;
    $[65] = comment.id;
    $[66] = handleReportSubmit;
    $[67] = isReportModalOpen;
    $[68] = panelHostEl;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => setShowReportConfirmationModal(false);
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== panelHostEl || $[72] !== showReportConfirmationModal) {
    t27 = <ReportConfirmationModal open={showReportConfirmationModal} onAction={t26} container={panelHostEl} />;
    $[71] = panelHostEl;
    $[72] = showReportConfirmationModal;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== t17 || $[75] !== t18 || $[76] !== t19 || $[77] !== t20 || $[78] !== t23 || $[79] !== t25 || $[80] !== t27) {
    t28 = <div className="flex h-fit w-full flex-col gap-2 pl-10.5 md:pl-16">{t17}{t18}{t19}{t20}{t23}{t25}{t27}</div>;
    $[74] = t17;
    $[75] = t18;
    $[76] = t19;
    $[77] = t20;
    $[78] = t23;
    $[79] = t25;
    $[80] = t27;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  let t29;
  if ($[82] !== isAddingComment) {
    t29 = isAddingComment && <LoadingDots size="md" />;
    $[82] = isAddingComment;
    $[83] = t29;
  } else {
    t29 = $[83];
  }
  let t30;
  if ($[84] !== children || $[85] !== t14 || $[86] !== t28 || $[87] !== t29) {
    t30 = <><div className="flex h-fit w-full flex-col">{t14}{t28}{t29}{children}</div></>;
    $[84] = children;
    $[85] = t14;
    $[86] = t28;
    $[87] = t29;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  return t30;
}
function _temp3(prev_0) {
  return !prev_0;
}
function _temp2(prev_2) {
  return !prev_2;
}
function _temp(prev) {
  return !prev;
}
export default CommentBlock;